/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import type { Personnel } from '../../../../shared/types/game';
import { PLANNING_GROUPE_KEYS, type PlanningGroupeKey } from '../OperationalPlanningModal';

interface PersonnelDetailsGeneralInfoProps {
  selectedPersonnel: Personnel;
  statutGeneral: string | null;
  statutPlanning: string | null;
  derniereIntervention: { date: string; heure: string } | null;
  posteNom: string | null;
  personnelGroupeSelect: '' | PlanningGroupeKey;
  savingGroupe: boolean;
  savePersonnelGroupe: (v: '' | PlanningGroupeKey) => Promise<void>;
  getStatutGeneralColors: (s: string | null) => any;
  getStatutPlanningColors: (s: string | null) => any;
  t: (key: string) => string;
}

/** Section « Informations générales » extraite de PersonnelDetailsModal. */
const PersonnelDetailsGeneralInfo: React.FC<PersonnelDetailsGeneralInfoProps> = ({
  selectedPersonnel,
  statutGeneral,
  statutPlanning,
  derniereIntervention,
  posteNom,
  personnelGroupeSelect,
  savingGroupe,
  savePersonnelGroupe,
  getStatutGeneralColors,
  getStatutPlanningColors,
  t,
}) => (
  <div className="mb-6">
    <h3 className="text-lg font-semibold text-white mb-4">Informations générales</h3>
    <div className="grid grid-cols-2 gap-4">
      <div className="bg-zinc-800 rounded-lg p-4">
        <p className="text-sm text-zinc-400 mb-3">Statut</p>
        <div className="space-y-2">
          <div>
            <span
              className="inline-block px-3 py-1 rounded text-sm font-medium"
              style={getStatutGeneralColors(statutGeneral)}
            >
              {statutGeneral || 'Non défini'}
            </span>
          </div>
          <div>
            <span
              className="inline-block px-3 py-1 rounded text-sm font-medium"
              style={getStatutPlanningColors(statutPlanning)}
            >
              {statutPlanning || 'Non défini'}
            </span>
          </div>
        </div>
      </div>
      <div className="bg-zinc-800 rounded-lg p-4">
        <p className="text-sm text-zinc-400 mb-2">Dernière intervention</p>
        {derniereIntervention ? (
          <p className="text-white text-sm font-medium">
            {derniereIntervention.date} à {derniereIntervention.heure}
          </p>
        ) : (
          <p className="text-zinc-500 text-sm italic">Aucune intervention terminée</p>
        )}
      </div>
      <div className="bg-zinc-800 rounded-lg p-4">
        <p className="text-sm text-zinc-400 mb-1">Âge</p>
        <p className="text-white font-semibold">{selectedPersonnel.age} ans</p>
      </div>
      <div className={`bg-zinc-800 rounded-lg p-4 ${posteNom ? 'border border-sky-500/30' : ''}`}>
        <p className="text-sm text-zinc-400 mb-1">Poste à responsabilité</p>
        <p className={`font-semibold ${posteNom ? 'text-sky-400' : 'text-zinc-500'}`}>
          {posteNom || 'Aucun'}
        </p>
      </div>
      {selectedPersonnel.grade_code && (
        <div className="bg-zinc-800 rounded-lg p-4">
          <p className="text-sm text-zinc-400 mb-1">Grade code</p>
          <p className="text-white font-semibold">{selectedPersonnel.grade_code}</p>
        </div>
      )}
      <div className="bg-zinc-800 rounded-lg p-4 sm:col-span-2">
        <p className="text-sm text-zinc-400 mb-2">
          {t('operationalPlanning.personnelDetailGroupe')}
        </p>
        <select
          className="w-full max-w-md rounded-lg border border-zinc-600 bg-zinc-900 px-3 py-2 text-sm text-white disabled:opacity-50"
          value={personnelGroupeSelect}
          disabled={savingGroupe}
          onChange={(e) => {
            const v = e.target.value;
            void savePersonnelGroupe(v === '' ? '' : (v as PlanningGroupeKey));
          }}
          aria-label={t('operationalPlanning.personnelDetailGroupe')}
        >
          <option value="">{t('operationalPlanning.personnelDetailGroupeNone')}</option>
          {PLANNING_GROUPE_KEYS.map((k) => (
            <option key={k} value={k}>
              {t(`operationalPlanning.filterGroupeGr${k}` as const)}
            </option>
          ))}
        </select>
        <p className="mt-2 text-xs text-zinc-500">
          {t('operationalPlanning.personnelDetailGroupeHint')}
        </p>
      </div>
    </div>
    {(selectedPersonnel as any).photo && (
      <div className="mt-4 bg-zinc-800 rounded-lg p-4">
        <p className="text-sm text-zinc-400 mb-2">Photo</p>
        <img
          src={(selectedPersonnel as any).photo}
          alt={`${selectedPersonnel.prenom} ${selectedPersonnel.nom}`}
          className="h-32 w-32 object-cover rounded-lg"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>
    )}
  </div>
);

export default PersonnelDetailsGeneralInfo;

